import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Monitor, MapPin, Calendar, ArrowLeft, Home, BookOpen } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JulieOptionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [hasGrevenDates, setHasGrevenDates] = useState(false);
  const [visibility, setVisibility] = useState({
    julie_online: true,
    julie_greven: true,
    julie_weekend_external: true,
    julie_weekend_greven: true,
    julie_theorie_clinic: true
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkGrevenAvailability();
    fetchVisibility();
  }, []);

  const fetchVisibility = async () => {
    try {
      const response = await axios.get(`${API}/services/visibility`);
      setVisibility(response.data);
    } catch (error) {
      console.error('Error fetching visibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkGrevenAvailability = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/availability/julie-greven-weekend?start_date=${today}`);
      setHasGrevenDates(response.data && response.data.length > 0);
    } catch (error) {
      console.error('Error checking Greven availability:', error);
      setHasGrevenDates(false);
    }
  };

  const options = [
    {
      id: 'online',
      visibilityKey: 'julie_online',
      title: t('Pivo Live Lesson'),
      subtitle: t('Remote Teaching - Wednesdays'),
      description: t('Pivo description'),
      icon: Monitor,
      path: '/teaching-julie/online',
      color: '#7c6f5e'
    },
    {
      id: 'greven',
      visibilityKey: 'julie_greven',
      title: t('On-Site Training'),
      subtitle: t('Live at JC-P Facility - Wednesdays'),
      description: t('On-Site description'),
      icon: MapPin,
      path: '/teaching-julie/greven',
      color: '#7c6f5e'
    },
    {
      id: 'weekend',
      visibilityKey: 'julie_weekend_external',
      title: t('Weekend Clinic'),
      subtitle: t('At Your Stable or Club'),
      description: t('Weekend description'),
      icon: Calendar,
      path: '/weekend-course-external',
      color: '#7c6f5e'
    },
    {
      id: 'weekend-greven',
      visibilityKey: 'julie_weekend_greven',
      title: t('Weekend Course in Greven'),
      subtitle: t('Here in Greven'),
      description: t('Weekend Greven description'),
      icon: Home,
      path: '/weekend-course-greven',
      color: '#7c6f5e'
    },
    {
      id: 'theorie-clinic',
      visibilityKey: 'julie_theorie_clinic',
      title: t('Theorie Clinic'),
      subtitle: t('Theoretical Workshops - Online or On-Site'),
      description: t('Theorie Clinic description'),
      icon: BookOpen,
      path: '/theorie-clinic',
      color: '#7c6f5e'
    }
  ];

  // Filter options based on visibility settings
  const visibleOptions = options.filter(option => {
    // Check visibility setting
    if (!visibility[option.visibilityKey]) {
      return false;
    }
    // Special case: Hide Greven weekend option if no dates available
    if (option.id === 'weekend-greven' && !hasGrevenDates) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="booking-page" style={{ paddingTop: '100px', textAlign: 'center' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button
          onClick={() => navigate('/')}
          className="back-arrow-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="booking-header">
          <h1 className="booking-title" data-testid="page-title">{t('Choose Your Training Type')}</h1>
          <p style={{ color: '#6b6b6b' }}>{t('Select how you\'d like to train')}</p>
          <p style={{ color: '#7c6f5e', marginTop: '1rem', fontSize: '1.125rem' }}>
            3x World Champion • 3x Nordic Champion • 26x Danish Champion
          </p>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {visibleOptions.map((option) => {
            const Icon = option.icon;
            return (
              <div
                key={option.id}
                onClick={() => navigate(option.path)}
                data-testid={`option-${option.id}`}
                style={{
                  background: 'white',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '8px',
                  padding: '2.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textAlign: 'center'
                }}
                className="instructor-card"
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: `${option.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem'
                }}>
                  <Icon size={40} color={option.color} />
                </div>
                <h2 style={{
                  fontSize: '1.75rem',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  color: '#2c2c2c',
                  fontFamily: "'Cormorant Garamond', serif"
                }}>
                  {option.title}
                </h2>
                {option.id === 'greven' && (
                  <p style={{ fontSize: '0.85rem', color: '#8b4857', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                    {t('New address note')}
                  </p>
                )}
                <p style={{
                  fontSize: '0.875rem',
                  color: '#7c6f5e',
                  fontWeight: 600,
                  marginBottom: '1rem'
                }}>
                  {option.subtitle}
                </p>
                <p style={{
                  fontSize: '1rem',
                  color: '#6b6b6b',
                  lineHeight: '1.6'
                }}>
                  {option.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JulieOptionsPage;